import React from 'react'
import { RootState, Subtask } from 'constants/interfaces'
import { Box, Flex, Icon, Text } from '@chakra-ui/core'
import { useSelector } from 'react-redux'
import useLocaleText from 'components/useLocaleText'
import { MdOutlineStar } from 'react-icons/md'
import { VotedParticipants } from './VotedParticipicans'
import { extractedUidsFromGroups } from 'utils'
import { isRtlSelector } from 'redux/selectors/general'
import { Title } from 'components/TasksV2/UI/Title'
import styles from './styles.module.css'
import { buildPollOptions } from 'components/TasksV2/TasksOverview/utils'
import { useTaskOverviewContext } from '../../TaskOverviewStackProvider'
import ExportToExcelButton from 'components/CommonComponents/ExportToExcelButton'
import { exportPollResultsToExcel } from './utils'
import { findLatestSubtaskResults } from '../../model/results'

interface IProps {
  pollSubtask: Subtask
}
export const VotersList = ({ pollSubtask }: IProps) => {
  const { options, tid, st_id, title } = pollSubtask
  const { task, isComeFromDashboard } = useTaskOverviewContext()

  const t_votes = useLocaleText('t_votes')
  const t_do_not_voted = useLocaleText('t_do_not_voted')
  const isRtl = useSelector(isRtlSelector)
  const { allResults } = useSelector((state: RootState) => state.tasks_v2)
  const {
    config: { groups: retailGroups, tags: retailTags, retailUsersObject },
    activeGroupID,
    userWriteTaskPermissions: { users: permittedUsers, groups: permittedGroups },
  } = useSelector((state: RootState) => state.config)
  const uid = useSelector((state: RootState) => state.auth.uid)
  const { tags, groups, users, is_individual_results, recurrence_ts } = task

  if (!options) return null

  const taskTags = retailTags.filter((tag) => tags?.includes(tag.sk))
  const taskTagUsers = taskTags.map((tag) => tag?.users ?? []).flat()
  const taskTagGroups = taskTags.map((tag) => tag?.groups ?? []).flat()

  const possibleVoters = !is_individual_results
    ? Array.from(new Set([...(groups ?? []), ...(users ?? []), ...taskTagGroups, ...taskTagUsers])).filter(
        (id) => Object.keys(retailUsersObject!).includes(id) || Object.keys(retailGroups).includes(id)
      )
    : (
        Array.from(
          new Set([
            ...users,
            ...taskTagUsers,
            ...extractedUidsFromGroups(groups ?? [], retailGroups),
            ...extractedUidsFromGroups(taskTagGroups, retailGroups),
          ])
        ) as string[]
      ).filter((id) => Object.keys(retailUsersObject!).includes(id) || Object.keys(retailGroups).includes(id))

  const currentPollResults = findLatestSubtaskResults(allResults[tid], st_id, recurrence_ts)

  const votedParticipants = currentPollResults
    ?.filter((result) => result?.poll_selections && result?.poll_selections?.length > 0)
    .map((result) => result.context_id)

  const notVotedParticipants = possibleVoters.filter((participant) => !votedParticipants?.includes(participant))
  const optionsArray = buildPollOptions({
    options,
    pollResults: currentPollResults,
    permittedUsers,
    permittedGroups,
    groups: retailGroups,
    retailUsersObject: retailUsersObject!,
  })

  const handleExportToExcel = () => {
    exportPollResultsToExcel({
      currentPollResults,
      notVotedParticipants,
      options,
      fileName: 'Poll_Results - ' + title,
      groups: retailGroups,
      retailUsersObject: retailUsersObject!,
    })
  }

  return (
    <>
      {currentPollResults.length > 0 && isComeFromDashboard && (
        <Flex mx="auto" mt="10px" mb="20px" justifyContent="center">
          <ExportToExcelButton actionWithoutArgs={handleExportToExcel} />
        </Flex>
      )}
      <div className={styles.list}>
        {optionsArray.map(({ label, count, currentQuestionParticipants }, index) => (
          <section key={index} className={styles.section}>
            <Flex
              flexDir="row"
              justifyContent="space-between"
              fontWeight="500"
              alignItems="center"
              className={styles.heading}
            >
              <Title title={label} minWidth={0} fontWeight="500" fontSize="14px" isTruncated />

              <Flex
                flexDir={isRtl ? 'row-reverse' : 'row'}
                style={{ columnGap: '5px' }}
                alignItems="center"
                fontSize="14px"
                minWidth={0}
                justifyContent="flex-end"
                className={styles.count}
              >
                {currentQuestionParticipants.some(
                  (participant) => participant.id === uid || participant.id === activeGroupID
                ) && <Icon as={MdOutlineStar} />}
                <Text ml={0}>
                  {count}/{possibleVoters.length}
                </Text>
                <Text>{t_votes}</Text>
              </Flex>
            </Flex>

            {currentQuestionParticipants.length > 0 && (
              <Box className={styles.participants}>
                <VotedParticipants participants={currentQuestionParticipants} />
              </Box>
            )}
          </section>
        ))}
        {notVotedParticipants.length > 0 && (
          <section className={styles.section}>
            <Flex
              className={styles.heading}
              flexDir="row"
              justifyContent="space-between"
              fontWeight="500"
              fontSize="14px"
            >
              <Title title={t_do_not_voted} minWidth={0} isTruncated fontWeight="500" fontSize="14px" />
              <Text className={styles.count} fontWeight="500" fontSize="14px">
                {notVotedParticipants.length}
              </Text>
            </Flex>

            {notVotedParticipants.length > 0 && (
              <Box className={styles.participants}>
                <VotedParticipants
                  participants={notVotedParticipants.map((id) => ({
                    id,
                  }))}
                />
              </Box>
            )}
          </section>
        )}
      </div>
    </>
  )
}
